import React, {ReactElement} from "react"
import {Resource} from "../../resources/common"
import {Customer} from "../../resources/customer"
import {AllowedCustomersColumns, CustomersColumns} from "./Customer.types"
import Icon from "../Icon/Icon"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import classNames from "classnames"
import {OrgName} from "../OrgName/OrgName"
import {kebabCase, startCase} from "lodash"
import moment from "moment"
import IdLink from "../IdLink/IdLink"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"

interface CustomerRowProps {
	customer: Customer
	include?: Resource[]
	includedColumns: Array<AllowedCustomersColumns>
	onSelect?: (customer: Customer) => void
}

export default function CustomerRow({customer, include, includedColumns, onSelect}: CustomerRowProps) {
	const navigateWithListPath = useNavigateWithListPath()

	const id = customer.id
	const org = customer.relationships.org?.data.id
	const status = customer.attributes.status
	const createdAt = customer.attributes.createdAt
	let name = ""
	let typeIcon: JSX.Element | null = null
	let typeLink = ""

	switch (customer.type) {
		case "businessCustomer":
			name = customer.attributes.name
			typeIcon = <Icon icon={"toolbox"} />
			typeLink = "business"
			break
		case "individualCustomer":
			name = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
			typeIcon = <Icon icon={"user-geometric-action---users"} />
			typeLink = "individual"
			break
		case "businessFBOCustomer":
			name = customer.attributes.name
			typeIcon = <Icon icon={"toolbox"} />
			typeLink = "businessFBO"
			break
		case "individualWalletCustomer":
			name = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
			typeIcon = <Icon icon={"user-geometric-action---users"} />
			typeLink = "individualWallet"
			break
		case "businessWalletCustomer":
			name = customer.attributes.businessName
			typeIcon = <Icon icon={"toolbox"} />
			typeLink = "businessWallet"
			break
	}

	const contentColumns: Partial<Record<AllowedCustomersColumns, ReactElement>> = {}

	if (includedColumns.includes(CustomersColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink className="link" href={`/${typeLink}/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CustomersColumns.type)) {
		contentColumns["Type"] = (
			<DataTableCell>
				<span className="icon">{typeIcon}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CustomersColumns.org)) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} element={DataTableCell} />
	}

	if (includedColumns.includes(CustomersColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<div className={classNames("customers-status", kebabCase(status))}>{startCase(status)}</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CustomersColumns.name)) {
		contentColumns["Name"] = <DataTableCell>{name}</DataTableCell>
	}

	if (includedColumns.includes(CustomersColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})
	return (
		<DataTableRow
			className={classNames(onSelect && "clickable-row")}
			onClick={(e) => {
				e.preventDefault()
				if (onSelect) {
					onSelect(customer)
				} else {
					navigateWithListPath(`/customers/${typeLink}/${id}`)
				}
			}}
		>
			{content}
		</DataTableRow>
	)
}
